import { formatCurrencyFlexible, formatCurrencyToShorthand } from 'utils/helpers';
import {
  shouldShowAmountRaised,
  type AmountVisibilityContext,
} from 'utils/offerings/amountRaisedVisibility';
import type {
  Company,
  OfferingCardData,
  OfferingSection,
} from 'components/HomepageV3/data/demoData';
import { isRegA, isRegCf, isRegD } from 'components/HomepageV3/utils/disclosures';
import type { LiveIndustry } from 'components/HomepageV3/utils/industries';

/**
 * Maps `/elastic-search/search/offering/search` results into the SE3 Explore
 * shapes (OfferingSection cards + companies + industry aggregation). This is the
 * FULL offering catalog (unlike the personalized tombstones feed, which is empty
 * for anonymous sessions), and elastic carries a clean `industries[]` per
 * offering plus an `aggregations.industries` bucket list — so the Explore
 * industry filter shows real industries with real counts (PD Paige QA).
 */

export interface ElasticOffering {
  id?: string;
  slug?: string;
  name?: string;
  tagline?: string;
  status?: string;
  regulationType?: string;
  offeringType?: string;
  industries?: string[];
  numberOfInvestors?: number;
  amountRaised?: string | number;
  /** Present on newer Elastic rows; use only this ceiling for Reg D availability. */
  maximumFundingAmount?: string | number;
  maximum_funding_amount?: string | number;
  minimumInvestmentAmount?: string | number;
  waitlist?: boolean;
  isWaitlist?: boolean;
  waitlisted?: boolean;
  lockedOnWaitlist?: boolean;
  locked_on_waitlist?: boolean;
  isOverMax?: boolean;
  is_over_max?: boolean;
  coverTombstoneUrl?: string | null;
  logoUrl?: string | null;
  issuerName?: string | null;
  entityName?: string | null;
  entity_name?: string | null;
  entity?: { legalName?: string | null; name?: string | null } | null;
  company?: { legalName?: string | null; name?: string | null } | null;
  startengineCompany?: { companyName?: string } | null;
  /** Funding close date (ISO). Elastic has no days_left, so days-left derives from this. */
  endDate?: string;
}

const legalRegDEntity = (offering?: ElasticOffering): string => {
  if (!offering) return '';
  return (
    [
      offering.issuerName,
      offering.entityName,
      offering.entity_name,
      offering.entity?.legalName,
      offering.entity?.name,
      offering.company?.legalName,
    ]
      .find((value) => value?.trim())
      ?.trim() ?? ''
  );
};

/**
 * Elastic only carries the customer-facing campaign name, while the offering
 * detail response carries the legal issuer series required by Reg D copy.
 * Apply that authoritative entity to every catalog company before cards render.
 */
export function hydrateRegDDisclosureEntities(
  companies: Record<string, Company>,
  authoritativeOfferings?: Map<string, ElasticOffering>,
): Record<string, Company> {
  if (!authoritativeOfferings?.size) return companies;

  return Object.fromEntries(
    Object.entries(companies).map(([key, company]) => {
      if (!company.regD) return [key, company];
      const companyName = legalRegDEntity(authoritativeOfferings.get(key));
      return [key, companyName ? { ...company, companyName } : company];
    }),
  );
}

export interface ElasticAggregations {
  /** Production returns `count`; older/test payloads may still use `doc_count`. */
  industries?: Array<{ key?: string; count?: number; doc_count?: number }>;
}

/** All tombstones with fewer than 30 days remaining receive this corner badge. */
const DAYS_LEFT_BADGE_EXCLUSIVE_MAX_DAYS = 30;

/** "50027102.31 USD" → 50027102.31 */
const parseUsd = (value?: string | number): number => {
  if (!value) return 0;
  const n = Number.parseFloat(String(value).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/**
 * Reg D availability must be based on its funding ceiling, never its minimum
 * investment. A missing Elastic ceiling is unknown availability, not evidence
 * that an offering is waitlisted.
 */
const regDFillPct = (offering: ElasticOffering): number | null => {
  const raised = parseUsd(offering.amountRaised);
  const maximum = parseUsd(offering.maximumFundingAmount ?? offering.maximum_funding_amount);
  if (maximum <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((raised / maximum) * 100)));
};

const isRegDWaitlisted = (offering: ElasticOffering): boolean =>
  Boolean(
    offering.waitlist ||
    offering.isWaitlist ||
    offering.waitlisted ||
    offering.lockedOnWaitlist ||
    offering.locked_on_waitlist ||
    offering.isOverMax ||
    offering.is_over_max,
  );

/**
 * Elastic's search documents do not consistently include Reg D's funding
 * ceiling. The primary offering endpoint does, so filtered Home/Explore cards
 * can be hydrated without changing the source or behavior of any other card.
 */
export function hydrateRegDAvailability(
  card: OfferingCardData,
  offering?: Pick<
    ElasticOffering,
    | 'amountRaised'
    | 'maximumFundingAmount'
    | 'maximum_funding_amount'
    | 'lockedOnWaitlist'
    | 'locked_on_waitlist'
    | 'isOverMax'
    | 'is_over_max'
    | 'waitlist'
    | 'isWaitlist'
    | 'waitlisted'
  >,
): OfferingCardData {
  if (!offering) return card;

  const fillPct = regDFillPct(offering);
  const waitlisted = isRegDWaitlisted(offering) || fillPct === 100;
  // Do not alter a card when the authoritative record cannot establish a
  // ceiling or a waitlist state. This deliberately leaves Reg CF/A+ and all
  // existing non-Reg-D behavior untouched.
  if (!waitlisted && fillPct === null) return card;

  const isUrgent = waitlisted || (fillPct ?? 0) >= 85;
  return {
    ...card,
    // A closing-soon corner token takes precedence over a waitlist token.
    ...(waitlisted && !card.badge ? { badge: { cls: 'bdg-gold', label: 'Waitlist' } } : {}),
    figR: {
      lbl: 'Availability',
      val: waitlisted ? 'Waitlist' : `${100 - (fillPct ?? 0)}% left`,
      cls: isUrgent ? 'urg' : 'ok',
    },
    fill: { w: waitlisted ? 100 : (fillPct ?? 0), cls: isUrgent ? 'urg' : 'ok' },
  };
}

const regLine = (reg?: string): string => {
  if (isRegCf(reg)) return 'Reg CF';
  if (isRegA(reg)) return 'Reg A+';
  return 'Reg D';
};

// Reg D disclosure entity. Elastic exposes only the customer-facing series
// (`name`), not the full legal series, so append "a series of StartEngine Private
// [Funds] LLC" — matching the /private wording and driving regDDisclosure's
// portfolio phrasing for funds. Idempotent if the suffix is already present.
const PRIVATE_SERIES_SUFFIX_RE = /a series of startengine private (funds )?llc/i;
const regDSeriesEntity = (name?: string): string => {
  const base = (name ?? '').trim();
  if (!base) return base;
  if (PRIVATE_SERIES_SUFFIX_RE.test(base)) return base;
  const suffix = /\bfunds?\b/i.test(base)
    ? ', a series of StartEngine Private Funds LLC'
    : ', a series of StartEngine Private LLC';
  return `${base}${suffix}`;
};

/** Days until the funding close date, or undefined when absent/past-parse. */
const elasticDaysLeft = (offering: ElasticOffering): number | undefined => {
  if (!offering.endDate) return undefined;
  const end = new Date(offering.endDate).getTime();
  if (!Number.isFinite(end)) return undefined;
  const days = Math.ceil((end - Date.now()) / 86_400_000);
  return days >= 0 ? days : undefined;
};

const daysLeftBadge = (offering: ElasticOffering): OfferingCardData['badge'] | undefined => {
  const days = elasticDaysLeft(offering);
  if (days === undefined || days >= DAYS_LEFT_BADGE_EXCLUSIVE_MAX_DAYS) return undefined;
  return {
    cls: 'bdg-wine',
    dot: true,
    label: `${days} ${days === 1 ? 'day' : 'days'} left`,
  };
};

/**
 * Card avatar/banner image. Crowdfunding (Reg CF/A+) renders the cover photo as
 * a top banner; everything else (Reg D) uses the ICON LOGO in the tile — falling
 * back to the cover only when no logo is published (Hannah QA). Logos show fully
 * (contain); cover photos fill (cover).
 */
function offeringImage(
  offering: ElasticOffering,
  isCf: boolean,
): Partial<Pick<Company, 'logo' | 'banner' | 'fit'>> {
  if (isCf) {
    return offering.coverTombstoneUrl
      ? { logo: offering.coverTombstoneUrl, banner: true, fit: 'cover' }
      : {};
  }
  const logo = offering.logoUrl || offering.coverTombstoneUrl;
  if (!logo) return {};
  return { logo, banner: false, fit: offering.logoUrl ? 'contain' : 'cover' };
}

/** Map one elastic offering → an SE3 card + company keyed by slug. */
export function mapElasticOffering(
  offering: ElasticOffering,
  amountVisibility: AmountVisibilityContext = { settingsLoaded: true },
): { card: OfferingCardData; company: Company } | null {
  const slug = offering.slug;
  if (!slug || !offering.name) return null;
  const reg = offering.regulationType;
  const regA = isRegA(reg);
  const isCf = isRegCf(reg) || regA;
  const regD = isRegD(reg);
  const invested = parseUsd(offering.amountRaised);
  const showInvested = shouldShowAmountRaised({
    slug,
    amountRaised: offering.amountRaised,
    ...amountVisibility,
  });
  const minimum = parseUsd(offering.minimumInvestmentAmount);
  const regDFill = regD ? regDFillPct(offering) : null;
  // Only an explicit offering status or a real fully-funded ceiling can put a
  // Reg D offering on the waitlist rail. Search matches must never turn every
  // private offering into a waitlist offering merely because Elastic omitted
  // a funding ceiling.
  const regDWaitlist = isRegDWaitlisted(offering) || regDFill === 100;
  const categories = (offering.industries ?? []).map((s) => (s ?? '').trim()).filter(Boolean);
  const badge = daysLeftBadge(offering);

  const card: OfferingCardData = {
    co: slug,
    // Keep the paired card figures compact. The full currency formatter can
    // produce values such as "$2,234,650.64", which collides with Minimum in
    // a fixed-width tombstone; control uses the shorthand amount instead.
    ...(showInvested
      ? { figL: { lbl: 'Invested', val: formatCurrencyToShorthand(invested) } }
      : {}),
    // Never derive Reg D availability from its funding minimum. When Elastic
    // includes the true ceiling, display the same truthful percent-left rail
    // as Home and Private; with no ceiling, retain a neutral Open state.
    figR: regD
      ? {
          lbl: 'Availability',
          val: regDWaitlist ? 'Waitlist' : regDFill === null ? 'Open' : `${100 - regDFill}% left`,
          cls: regDWaitlist || (regDFill ?? 0) >= 85 ? 'urg' : 'ok',
        }
      : minimum > 0
        ? { lbl: 'Minimum', val: formatCurrencyFlexible(Math.round(minimum)), cls: 'amber' }
        : { lbl: 'Availability', val: 'Open', cls: 'ok' },
    // Only render a progress bar when it carries real meaning: a truthful
    // raised/ceiling percent, or a full bar for a waitlisted (effectively full)
    // raise. An "Open" Reg D with no known ceiling has no percent to show, so it
    // omits the bar (OfferingCard reserves the space) rather than painting a
    // misleading empty 0% track (Paige QA 2026-07-15).
    ...(regD && (regDWaitlist || regDFill !== null)
      ? {
          fill: {
            w: regDWaitlist ? 100 : (regDFill ?? 0),
            cls: regDWaitlist || (regDFill ?? 0) >= 85 ? ('urg' as const) : ('ok' as const),
          },
        }
      : {}),
    ...(categories.length ? { categories } : {}),
    // A closing-soon corner badge takes precedence. Otherwise actual waitlist
    // offerings receive the status token the search results were missing.
    ...(badge ? { badge } : regDWaitlist ? { badge: { cls: 'bdg-gold', label: 'Waitlist' } } : {}),
    // Reg D deliberately does NOT set card.disclosure: OfferingCard then derives
    // the disclosure from company.companyName via the shared liveDisc path AND
    // renders the "See full details in footnote 1." link — matching the /private
    // and browse tombstones (Paige QA 2026-07-15). Setting card.disclosure here
    // suppressed that footnote and dropped the "a series of StartEngine Private
    // LLC" entity wording.
  };

  const company: Company = {
    key: slug,
    name: offering.name,
    series: regLine(reg),
    sector: regD ? '' : (categories[0] ?? ''),
    invested: showInvested ? formatCurrencyToShorthand(invested) : '',
    desc: offering.tagline ?? '',
    reg: regLine(reg),
    regcf: isCf,
    regA,
    regD,
    // The Reg D disclosure entity is the offering's SERIES ("… a series of
    // StartEngine Private LLC"), not the broker-dealer. Elastic carries the
    // customer-facing series in `name`; append the legal suffix so liveDisc
    // reads like the /private card (fund names get the portfolio wording).
    ...(regD ? { companyName: regDSeriesEntity(offering.name) } : {}),
    href: `/offering/${slug}`,
    ...offeringImage(offering, isCf),
  };

  return { card, company };
}

/** Full elastic catalog → an SE3 "All offerings" section + companies map. */
export function mapElasticCatalog(
  list: ElasticOffering[],
  amountVisibility: AmountVisibilityContext = { settingsLoaded: true },
): {
  sections: OfferingSection[];
  companies: Record<string, Company>;
} {
  const cards: OfferingCardData[] = [];
  const companies: Record<string, Company> = {};
  const seen = new Set<string>();
  list.forEach((offering) => {
    const mapped = mapElasticOffering(offering, amountVisibility);
    if (!mapped || seen.has(mapped.card.co)) return;
    seen.add(mapped.card.co);
    cards.push(mapped.card);
    companies[mapped.company.key] = mapped.company;
  });
  const sections: OfferingSection[] = [
    { eyebrow: 'All offerings', title: 'All offerings', desc: '', cards },
  ];
  return { sections, companies };
}

const TRENDING_LIMIT = 15;
const CLOSING_LIMIT = 15;

/**
 * Build the SE3 browse shelves (Trending / Closing soon / Low minimum) from the
 * FULL elastic catalog, so the shelves surface the whole currently-raising set
 * (15 / 15 / all) rather than the 3-per-row /tombstones feed (Josh QA). The list
 * arrives momentum-sorted (sortBy=mostMomentum). Elastic has no funding ceiling,
 * so there is no funding-completion value to infer. Every catalog card derives
 * its top-right days-left badge from endDate when it is fewer than 30 days out.
 */
export function buildSe3ShelvesFromElastic(
  list: ElasticOffering[],
  {
    excludeRegD = false,
    hiddenSlugs = [],
    amountVisibility = { settingsLoaded: true },
  }: {
    excludeRegD?: boolean;
    hiddenSlugs?: string[];
    amountVisibility?: AmountVisibilityContext;
  } = {},
): {
  sections: OfferingSection[];
  fullSections: OfferingSection[];
  companies: Record<string, Company>;
} {
  const companies: Record<string, Company> = {};
  type Row = { offering: ElasticOffering; card: OfferingCardData; days?: number };
  const rows: Row[] = [];
  const seen = new Set<string>();
  const hidden = new Set(hiddenSlugs.map((s) => s.trim().toLowerCase()).filter(Boolean));
  list.forEach((offering) => {
    // Reg D stays in the catalog even for retail (see shelfRows below); honor the
    // hidden-slugs kill-switch.
    if (offering.slug && hidden.has(offering.slug.toLowerCase())) return;
    const mapped = mapElasticOffering(offering, amountVisibility);
    if (!mapped || seen.has(mapped.card.co)) return;
    seen.add(mapped.card.co);
    companies[mapped.company.key] = mapped.company;
    rows.push({ offering, card: mapped.card, days: elasticDaysLeft(offering) });
  });

  const isCrowd = (reg?: string): boolean => isRegCf(reg) || isRegA(reg);

  // Retail (logged-in, non-accredited) must not see Reg D (506c is accredited-
  // only) in the momentum / closing / low-minimum SHELVES. But Reg D private
  // tombstones must remain in the full catalog + companies so they still surface
  // under the "$5K+" minimum filter (Paige QA 2026-07-15): hiding them from the
  // shelves is right, dropping them from the catalog is not.
  const shelfRows = excludeRegD ? rows.filter((r) => !isRegD(r.offering.regulationType)) : rows;

  // Shelves are INDEPENDENT lenses on the same catalog (no cross-shelf dedup):
  // an offering can be both trending AND closing soon, so each shelf stays
  // populated (cross-dedup starved Closing when high-momentum offerings were also
  // closing soon).

  // Trending: momentum order (list order) — top 15.
  const trending = shelfRows.slice(0, TRENDING_LIMIT);

  // Closing soon: the 15 soonest-closing offerings (by end date), like Trending's
  // top-15 (Josh QA: Closing soon should count 15, not only those within ~30
  // days). The shelf itself still retains the 15 soonest offerings; the card
  // badge has already been applied consistently while mapping the full catalog.
  const closing = shelfRows
    .filter((r) => typeof r.days === 'number')
    .sort((a, b) => (a.days as number) - (b.days as number))
    .slice(0, CLOSING_LIMIT);

  // Low minimum: all crowdfunding (Reg CF/A). The shelf changes only the
  // offering selection; cards retain the common Invested → Minimum layout.
  const lowMin = shelfRows.filter((r) => isCrowd(r.offering.regulationType)).map((row): Row => row);

  const mkSection = (
    eyebrow: string,
    title: string,
    desc: string,
    shelfRows: Row[],
  ): OfferingSection => ({ eyebrow, title, desc, cards: shelfRows.map((r) => r.card) });

  const shelves: OfferingSection[] = [
    mkSection('Most momentum', 'Trending now', 'Highest investment activity recently', trending),
    mkSection(
      'Limited availability',
      'Closing soon',
      'Due to least inventory or closing soon',
      closing,
    ),
    mkSection('Accessible investing', 'Low minimum offerings', 'Crowdfunding offerings', lowMin),
  ].filter((s) => s.cards.length > 0);

  // "All offerings" is a hidden counting section (never a visible shelf — browse
  // rows come from SIDE_GROUPS, cards from /tombstones). The sidebar's min-bucket
  // count dedupes across sections, so without the full catalog here "$5K+" only
  // counts the Reg D offerings that happened to land in a top-15 shelf (Josh QA).
  const catalog = mkSection('All offerings', 'All offerings', '', rows);
  const fullSections = shelves.length > 0 ? [...shelves, catalog] : [];

  return { sections: shelves, fullSections, companies };
}

/**
 * Compose an uncapped filter catalog with the richer live tombstone feed.
 * The curated Trending and Closing rows are special: private/Reg D cards may
 * not exist in Elastic's ranking, so retain the authoritative tombstone
 * ordering first and use Elastic only to fill the remainder of the 15-card
 * catalog. Other filters retain Elastic's full ordering while replacing
 * overlapping cards with their authoritative live data.
 */
export function mergeSe3FilteredSections(
  catalogSections: OfferingSection[] | undefined,
  liveSections: OfferingSection[] | undefined,
): OfferingSection[] | undefined {
  if (!catalogSections) return liveSections;

  return catalogSections.map((catalogSection) => {
    const liveSection = liveSections?.find((section) => section.title === catalogSection.title);
    if (!liveSection?.cards.length) return catalogSection;

    const section = {
      ...catalogSection,
      eyebrow: liveSection.eyebrow ?? catalogSection.eyebrow,
      desc: liveSection.desc ?? catalogSection.desc,
      tip: liveSection.tip ?? catalogSection.tip,
    };

    const liveBySlug = new Map(liveSection.cards.map((card) => [card.co, card]));
    const preservesCuratedOrdering =
      catalogSection.title === 'Trending now' || catalogSection.title === 'Closing soon';
    if (!preservesCuratedOrdering) {
      return {
        ...section,
        cards: catalogSection.cards.map((card) => liveBySlug.get(card.co) ?? card),
      };
    }

    const seen = new Set<string>();
    const cards = [...liveSection.cards, ...catalogSection.cards].filter((card) => {
      if (seen.has(card.co)) return false;
      seen.add(card.co);
      return true;
    });
    // Trending's sidebar count and "View all" contract are always the top
    // fifteen. Keep every curated `most_momentum_se3` card in its API order,
    // then append the Elastic ranking only until that cap is reached.
    const limit =
      catalogSection.title === 'Trending now'
        ? TRENDING_LIMIT
        : catalogSection.cards.length || cards.length;
    return { ...section, cards: cards.slice(0, limit) };
  });
}

/** Industry filter rows from elastic's `aggregations.industries` (real counts). */
export function elasticIndustries(aggregations?: ElasticAggregations): LiveIndustry[] {
  const buckets = aggregations?.industries ?? [];
  return buckets
    .map((b) => ({
      label: (b.key ?? '').trim(),
      key: (b.key ?? '').trim(),
      count: b.count ?? b.doc_count ?? 0,
    }))
    .filter((i) => i.label)
    .sort((a, b) => a.label.localeCompare(b.label));
}
